import { screenLg } from 'constants/styles/breakpoints';
import styled from 'styled-components';
import Icon from 'components/icons';

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)},${parseInt(result[3], 16)}`
    : null;
}

export const Container = styled.div<{ clickable?: boolean }>`
  background-color: #ffffff;
  border-radius: 12px;
  height: 478px;
  width: 360px;
  box-shadow: 0px 6px 18px 0px rgba(104, 84, 189, 0.08);
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  ${screenLg} {
    width: 420px;
  }

  ${({ clickable }) =>
    clickable &&
    `
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0px 10px 30px 0px rgba(104, 84, 189, 0.16);
    }
  `}
`;

export const Header = styled.div<{ color?: string; isSonar: boolean }>`
  position: relative;
  background: ${({ isSonar, color }) =>
    isSonar
      ? 'linear-gradient(90deg, #2f0559 4.2%, #863ad2 52.87%, #b736e4 97.03%)'
      : `rgba(${hexToRgb(color)}, 0.5)`};
  width: 100%;
  height: 136px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  border-start-start-radius: 12px;
  border-start-end-radius: 12px;

  ${screenLg} {
    height: 158px;
  }
`;

export const DurationPill = styled.span<{ bgColor: string }>`
  position: absolute;
  top: 10px;
  right: 10px;

  font-weight: 400;
  font-size: 12px;
  line-height: 14px;

  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  background: ${({ bgColor }) => bgColor || '#000'};
  color: ${({ theme }) => theme.default.colors.fonts.white};
  box-shadow: 0px 0px 10px rgba(167, 147, 246, 0.5);
  border-radius: 30px;
`;

export const DurationPillSpinnerContainer = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  position: relative;
  flex: 0 0 14px;
  overflow: hidden;

  > div {
    position: relative;
    width: 14px;
    height: 14px;
    left: 0;
    top: 0;
    transform: none;
  }

  > div > img {
    display: block;
    position: static;
    width: 14px;
    height: 14px;
    box-sizing: border-box;
    padding-right: 0;
    margin-right: 0;
    border-right: 0;
    transform-origin: center;
    transform: none;
    animation: duration-pill-spinner 1.2s linear infinite;
  }

  @keyframes duration-pill-spinner {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export const DurationPillUpdatingText = styled.span`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;

  &::after {
    content: '...';
    display: inline-block;
    width: 0;
    overflow: hidden;
    animation: duration-pill-dots 1.2s steps(4, end) infinite;
  }

  @keyframes duration-pill-dots {
    0% {
      width: 0;
    }
    100% {
      width: 3ch;
    }
  }
`;

export const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 320px;
  padding: 25px 15px 15px;
  ${screenLg} {
    padding: 35px;
  }
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: nowrap;
  min-height: 28px;
  margin: 0 0 10px;
`;

export const AccountBadgeWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

export const TitleText = styled.div`
  min-width: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  color: #1a2535;
  margin: 0;
  max-height: 48px;
`;

export const Description = styled.div`
  font-style: normal;
  font-size: 16px;
  color: #aeaeb2;
  line-height: 24px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
  max-height: 72px;
`;

export const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const DateContainer = styled.div`
  font-style: normal;
  font-weight: 450;
  font-size: 14px;
  line-height: 20px;
  color: #57575c;
  display: flex;
  flex-direction: column;
  flex: 1 1 40%;

  .playlist-card__created-by {
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }

  &.playlist-card__dates {
    flex: 0 0 auto;
    min-width: max-content;
  }

  .playlist-card__date-row {
    white-space: nowrap;
  }
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  text-transform: uppercase;
  font-weight: bold;
  color: #57575c;
  flex: 1 1 50%;
  justify-content: flex-end;
`;

export const PlaylistInfo = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: auto;

  .playlist-card__bottom-info {
    display: flex;
    gap: 0 13px;
    justify-content: space-between;
    ${screenLg} {
      gap: 0 25px;
    }
  }
`;

export const Divider = styled.div<{ flat?: boolean }>`
  border-radius: 6px;
  height: 1px;
  width: 100%;
  display: block;
  margin: ${({ flat }) => (!flat ? ' 25px 0 20px' : '25px 0 0')};
  background: ${({ flat }) => (!flat ? '#d0d2db' : 'transparent')};
`;

export const ButtonsGapWrapper = styled.div<{ hasPrimary?: boolean }>`
  display: grid;
  grid-template-columns: ${({ hasPrimary }) => (hasPrimary ? '6fr 1fr' : '1fr')};
  grid-template-rows: 1fr;
  grid-column-gap: ${({ hasPrimary }) => (hasPrimary ? '10px' : '0px')};
  grid-row-gap: 0px;
  justify-items: ${({ hasPrimary }) => (hasPrimary ? 'stretch' : 'end')};
  width: 100%;
`;

export const RecommendedDescription = styled.div`
  font-style: normal;
  font-size: 16px;
  line-height: 24px;
  color: #aeaeb2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 80%;
`;

export const RecommendedDescriptionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 50px;
  margin: 0 0 auto;
`;

export const AssignedMessage = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  text-transform: none;
  gap: 1px;
  span {
    font-size: 14px;
    margin-left: 1px;
    color: ${({ theme }) => theme.default.colors.performance.positiveDark};
  }
`;

export const IconButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 7px;
  justify-content: flex-end;
`;

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

export const ActionButtonsSlot = styled.div`
  display: flex;
  justify-content: flex-end;
  flex: 1;
`;

export const AccountHeaderLogo = styled.img`
  width: 96px;
  height: 96px;
  object-fit: contain;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.6);
  padding: 8px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.08);
`;

export const InfoIcon = styled(Icon)<{ fill?: string }>`
  fill: ${({ theme }) => theme.default.colors.secondaryPallete.purple.light};
  &:hover {
    fill: ${({ theme }) => theme.default.colors.secondaryPallete.purple.base};
  }
`;
